import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, RotateCcw, MessageSquare, Flag, Eye, Check, X, Award, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { startPracticeChat, sendPracticeChat, finishPracticeChat, getPracticeChat, reratePracticeChat, getMyPracticeChats,
  startPickQuiz, submitPickQuiz, getCopyFlags, closeCopyFlag } from "@/lib/api";

// Accountability coach course (Coaching Conversations). Coaches write, so nothing here uses the mic:
// levels 1-2 pick the right reply, level 3 writes one reply, levels 4-5 are practice chats on cards built
// to trip one failure each, marked on five questions. Green means right, red means wrong. The evidence and the
// coach's own words are what they learn from, so they're shown under every mark.

const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const QUESTIONS = [
  ["q1_the_number", "The protein number, against their target"],
  ["q2_used_their_words", "Used something only they told you"],
  ["q3_one_ask", "Exactly one ask: specific, measured, timed"],
  ["q4_caught_the_trap", "Caught what the card was hiding"],
  ["q5_sendable", "Sendable as a chat message"],
];
const markColour = (v) => (v === 2 ? "text-emerald-300" : v === 1 ? "text-amber-300" : "text-rose-300");

function ProteinTable({ table, foods }) {
  const [open, setOpen] = useState(false);
  if (!table && !foods) return null;
  const rows = foods ? Object.entries(foods).map(([food, f]) => [food, f]) : Object.entries(table).map(([food, g]) => [food, { protein_g: g }]);
  return (
    <div className="text-sm">
      <button type="button" onClick={() => setOpen(!open)} className="text-emerald-300 hover:text-emerald-200">
        {open ? "Hide" : "Show"} the food table</button>
      {open && (
        <div className="mt-2 rounded-lg border border-[#262626] p-3 max-h-72 overflow-y-auto">
          {foods && <p className="text-xs text-zinc-500 mb-2">Values from FatSecret India. Tap a food to see its FatSecret page.</p>}
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-0.5">
            <span className="text-xs text-zinc-500">Food</span><span className="text-xs text-zinc-500 text-right">Protein</span><span className="text-xs text-zinc-500 text-right">kcal</span>
            {rows.sort((a, b) => b[1].protein_g - a[1].protein_g).map(([food, f]) => (
              <div key={food} className="contents">
                <span className="text-zinc-300">{f.source ? <a href={f.source} target="_blank" rel="noreferrer" className="hover:underline">{food}</a> : food}
                  {f.serving ? <span className="text-zinc-500"> · {f.serving}</span> : null}</span>
                <span className="tabular-nums text-zinc-200 text-right">{f.protein_g} g</span>
                <span className="tabular-nums text-zinc-400 text-right">{f.kcal ?? ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CardInfo({ card }) {
  const p = card.person, t = card.targets;
  return (
    <div className="rounded-lg border border-[#2a2a2a] p-3 text-sm space-y-1">
      <div className="font-semibold text-zinc-100">{p.name} · {p.age} · {p.sex} · {p.weight_kg} kg · day {card.day}</div>
      <div className="text-zinc-400">Goal: {p.goal}</div>
      <div className="font-mono text-[13px] text-zinc-300">Set for {p.sex === "female" ? "her" : "him"}: {t.kcal} kcal · <b className="text-emerald-300">{t.protein_g} g protein</b> · {t.carbs_g} g carbs · {t.fat_g} g fat</div>
    </div>
  );
}

function CardDay({ card }) {
  return (
    <div className="space-y-1.5 text-sm">
      {card.meals.length ? (<>
        <div className="flex flex-wrap gap-2">{card.meals.map((m) => <MealPhoto key={m} text={m.replace(":", " 📷")} />)}</div>
        <p className="text-zinc-400">Sleep {card.sleep} · Steps {card.steps?.toLocaleString()}</p>
      </>) : null}
      {card.said && <p className="border-l-2 border-emerald-500 pl-3 italic text-zinc-200">"{card.said}"</p>}
      {card.extra && <p className="rounded-lg bg-rose-500/10 border border-rose-500/20 p-3 text-zinc-200">{card.extra}</p>}
    </div>
  );
}

// Meals arrive the way members send them: as photos. Each one is a tile with the food on it and the
// caption the member would type.
const FOOD = [["egg", "🥚"], ["chicken", "🍗"], ["fish", "🐟"], ["paneer", "🧀"], ["tofu", "🧀"], ["soya", "🫘"], ["whey", "🥤"],
  ["shake", "🥤"], ["milk", "🥛"], ["curd", "🥛"], ["yoghurt", "🥛"], ["biryani", "🍛"], ["dal", "🍲"], ["rajma", "🍲"], ["chole", "🍲"],
  ["sambar", "🍲"], ["khichdi", "🍲"], ["curry", "🍛"], ["rice", "🍚"], ["pulao", "🍚"], ["idli", "🍙"], ["dosa", "🫓"], ["roti", "🫓"],
  ["paratha", "🫓"], ["naan", "🫓"], ["poha", "🍚"], ["upma", "🍚"], ["toast", "🍞"], ["pizza", "🍕"], ["maggi", "🍜"], ["banana", "🍌"],
  ["apple", "🍎"], ["salad", "🥗"], ["vegetables", "🥦"], ["bhindi", "🥒"], ["coffee", "☕"], ["chai", "☕"], ["pickle", "🫙"], ["chutney", "🫙"]];
const foodIcons = (items) => items.split(",").map((it) => (FOOD.find(([k]) => it.toLowerCase().includes(k)) || [null, "🍽️"])[1]);

export function MealPhoto({ text }) {
  const [slot, items] = text.split(" 📷 ");
  return (
    <div className="w-52 max-w-full rounded-lg bg-[#202c33] p-1" data-testid="meal-photo">
      <div className="h-24 rounded-md bg-gradient-to-br from-amber-900/50 via-stone-800 to-stone-900 flex flex-wrap items-center justify-center gap-1 p-2 text-3xl">
        {foodIcons(items).map((e, i) => <span key={i}>{e}</span>)}
      </div>
      <div className="px-2 pt-1.5 pb-1 text-[14px] leading-snug text-zinc-100"><span className="text-zinc-400">{slot}:</span> {items}</div>
    </div>
  );
}

export function Thread({ chat }) {
  const end = useRef(null);
  useEffect(() => { if (chat.messages.some((m) => m.role === "coach")) end.current?.scrollIntoView({ block: "nearest" }); }, [chat.messages]);
  return (
    <div className="rounded-xl bg-[#0b141a] border border-[#1f2c34] p-3 sm:p-4 space-y-2 max-h-[55vh] overflow-y-auto" data-testid="chat-thread">
      {chat.messages.map((m, i) => {
        const coach = m.role === "coach";
        if (!coach && m.text.startsWith("(")) return <p key={i} className="text-center text-xs text-zinc-500 italic py-1">{m.text.slice(1, -1)}</p>;
        if (!coach && m.text.includes(" 📷 ")) return <div key={i} className="flex justify-start"><MealPhoto text={m.text} /></div>;
        return (
          <div key={i} className={`flex ${coach ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-[15px] leading-snug whitespace-pre-wrap ${coach ? "bg-[#005c4b] text-white" : "bg-[#202c33] text-zinc-100"}`}>{m.text}</div>
          </div>
        );
      })}
      <div ref={end} />
    </div>
  );
}

export function Marks({ r, trap }) {
  if (!r) return null;
  return (
    <div className="space-y-4" data-testid="chat-score">
      <div className="flex flex-wrap items-end gap-3">
        <div className="font-display text-5xl font-bold tabular-nums">{r.overall_score}<span className="text-xl text-zinc-500">/100</span></div>
        {r.total != null && !r.cards && <span className="text-sm text-zinc-400">{r.total}/10</span>}
      </div>
      {r.capped && <p className="text-sm text-amber-300">{r.capped}</p>}
      {trap && <p className="text-sm text-zinc-300 flex gap-2"><Eye className="h-4 w-4 mt-0.5 shrink-0 text-zinc-500" /><span><b>What this card was hiding:</b> {trap}</span></p>}
      <div className="space-y-2">
        {QUESTIONS.filter(([k]) => r[k]).map(([k, label]) => (
          <div key={k} className="rounded-lg border border-[#262626] p-3">
            <div className="flex justify-between gap-3 text-sm"><span className="font-semibold">{label}</span><span className={`tabular-nums font-semibold ${markColour(r[k].value)}`}>{r[k].value}/2</span></div>
            <p className="text-sm text-zinc-400 mt-1">{r[k].evidence}</p>
          </div>
        ))}
      </div>
      {!!r.what_worked?.length && <div><div className="eyebrow mb-1 text-emerald-400">What worked</div><ul className="list-disc pl-5 text-sm text-zinc-200 space-y-1">{r.what_worked.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
      {!!r.what_to_fix?.length && <div><div className="eyebrow mb-1 text-rose-400">What to fix</div><ul className="list-disc pl-5 text-sm text-zinc-200 space-y-1">{r.what_to_fix.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
      {r.this_week && <div><div className="eyebrow mb-1">This week</div><p className="text-sm text-zinc-200">{r.this_week}</p></div>}
      {!!r.flags?.length && <p className="text-sm text-zinc-400 flex items-center gap-1.5"><Flag className="h-4 w-4" />{r.flags.join(", ").replaceAll("_", " ")}</p>}
    </div>
  );
}

// Level 3 (one written reply, marked straight away) and levels 4-5 (a practice chat) for a unit.
export default function ChatRolePlay({ part, level = 4, title, passMark = 80, onExit }) {
  const [chat, setChat] = useState(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);

  const poll = useCallback((id) => {
    let tries = 0;
    const tick = () => getPracticeChat(id).then((c) => { setChat(c); if (c.status === "rating" && ++tries < 60) setTimeout(tick, 3000); })
      .catch(() => setTimeout(tick, 5000));
    tick();
  }, []);
  const start = async () => {
    setStarting(true);
    try { setChat(await startPracticeChat(part, level)); setNote(""); } catch (e) { err(e, "Couldn't start the chat"); }
    setStarting(false);
  };
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    const mine = text;
    setChat((c) => ({ ...c, messages: [...c.messages, { role: "coach", text: mine }] }));
    setText("");
    try { const c = await sendPracticeChat(chat.id, mine, level === 3 ? note : ""); setChat(c); if (c.status === "rating") poll(c.id); } catch (e) { err(e, "Couldn't send"); }
    setBusy(false);
  };
  const finish = async () => {
    try { const c = await finishPracticeChat(chat.id, { note }); setChat(c); poll(c.id); } catch (e) { err(e, "Couldn't end the chat"); }
  };
  const passed = chat?.rating && chat.rating.overall_score >= passMark;

  return (
    <div className="space-y-4" data-testid="chat-roleplay">
      {onExit && <button type="button" onClick={() => onExit()} className="text-sm text-zinc-400 hover:text-zinc-200">← All units</button>}
      <section className="card-surface p-5 sm:p-6 space-y-4">
        <div>
          <div className="eyebrow">Unit {part} · Level {level}: {level === 3 ? "write one reply" : level === 5 ? "hard chat" : "practice chat"}</div>
          <h2 className="font-display text-xl font-bold mt-1">{title || chat?.part_title}</h2>
        </div>
        {!chat && (<>
          <p className="text-sm text-zinc-400 max-w-2xl">{level === 3
            ? "You get one member's day, built to trip the mistake this unit is about. Write the one reply you would really send at 7 AM. It's marked as soon as you send it."
            : "You get one member's day, built to trip the mistake this unit is about. Write the reply you would really send at 7 AM. The member answers; keep going if you need to, then end the chat to get your marks."}
            {level === 5 ? " This member is tired and pushes back." : ""}</p>
          <p className="text-sm text-amber-300">An AI checker reads every reply. Write it yourself, about this member's day.</p>
          <Button onClick={start} disabled={starting} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6" data-testid="chat-start">
            {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquare className="h-4 w-4 mr-2" />}Start the chat</Button>
        </>)}
        {chat && (<>
          <p className="text-sm text-zinc-200">{chat.brief}</p>
          <CardInfo card={chat.card} />
          <ProteinTable table={chat.protein_table} foods={chat.food_table} />
          <Thread chat={chat} />
          {chat.status === "live" && (
            <div className="space-y-2">
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Type your reply…" data-testid="chat-input"
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} />
              {level === 3 && (
                <div className="space-y-1">
                  <label className="text-sm text-zinc-300" htmlFor="chat-note-3">What else do you do today? (not sent to the member)</label>
                  <Textarea id="chat-note-3" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="chat-note-3"
                    placeholder="e.g. tell my Challenge Manager, call them after the session…" />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={send} disabled={busy || !text.trim()} className="rounded-full" data-testid="chat-send">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}{level === 3 ? "Send for marking" : "Send"}</Button>
                <span className="text-xs text-zinc-500">{chat.coach_messages}/{chat.max_coach_messages} messages · {text.trim() ? text.trim().split(/\s+/).length : 0} words</span>
              </div>
              {!!chat.coach_messages && level !== 3 && (
                <div className="pt-2 space-y-2">
                  <label className="text-sm text-zinc-300" htmlFor="chat-note">What else do you do today? (not sent to the member)</label>
                  <Textarea id="chat-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. tell my Challenge Manager, call them after the session…" />
                  <Button variant="outline" onClick={finish} className="rounded-full" data-testid="chat-finish">End and get my marks</Button>
                </div>
              )}
            </div>
          )}
          {chat.status === "rating" && <p className="flex items-center gap-2 text-zinc-300"><Loader2 className="h-4 w-4 animate-spin" />Marking your reply. About a minute.</p>}
          {chat.status === "error" && <div className="space-y-2"><p className="text-rose-300">{chat.error}</p>
            <Button onClick={() => reratePracticeChat(chat.id).then(() => poll(chat.id))}>Try marking again</Button></div>}
          {chat.status === "rated" && (<>
            <span className={`inline-block px-3 py-1 rounded-full border text-sm font-semibold ${passed ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-rose-500/15 text-rose-300 border-rose-500/30"}`}>{passed ? "Passed" : "Try again"}</span>
            <Marks r={chat.rating} trap={chat.trap} />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { setChat(null); setText(""); setNote(""); }} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />{level === 3 ? "Try another" : "New card"}</Button>
              {passed && level < 5 && onExit && <Button onClick={() => onExit(level + 1)} className="rounded-full bg-emerald-600 hover:bg-emerald-500">
                Level {level + 1}: {level === 3 ? "practice chat" : "hard chat"}</Button>}
              {onExit && <Button variant="outline" onClick={() => onExit()} className="rounded-full">All units</Button>}
            </div>
          </>)}
        </>)}
      </section>
    </div>
  );
}

// Levels 1-2: pick the right reply. Five member days; 3 replies to choose from at level 1, 4 close ones at
// level 2. Marked at once, with why each wrong reply is wrong.
export function PickQuiz({ part, level, title, passMark = 80, onExit }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const exit = useRef(onExit);
  exit.current = onExit;
  const start = useCallback(() => {
    setResult(null); setAnswers({}); setQuiz(null);
    startPickQuiz(part, level).then(setQuiz).catch((e) => { err(e, "Couldn't start"); exit.current?.(); });
  }, [part, level]);
  useEffect(() => { start(); }, [start]);
  const submit = async () => {
    setBusy(true);
    try { setResult(await submitPickQuiz(quiz.id, answers)); window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { err(e, "Couldn't mark it"); }
    setBusy(false);
  };
  const byId = Object.fromEntries((result?.results || []).map((r) => [r.id, r]));
  return (
    <div className="space-y-4" data-testid="pick-quiz">
      {onExit && <button type="button" onClick={() => onExit()} className="text-sm text-zinc-400 hover:text-zinc-200">← All units</button>}
      <section className="card-surface p-5 sm:p-6 space-y-2">
        <div className="eyebrow">Unit {part} · Level {level}: {level === 1 ? "pick the right reply" : "pick the best reply"}</div>
        <h2 className="font-display text-xl font-bold">{title || quiz?.part_title}</h2>
        <p className="text-sm text-zinc-400 max-w-2xl">{level === 1
          ? "Five members, five days. For each one, pick the reply you would send. Get 4 of 5 right to open level 2."
          : "Harder. Every reply is close; only one follows all the rules. Get 4 of 5 right to open level 3, where you write your own."}</p>
        {result && (
          <div className={`rounded-lg border p-4 mt-2 ${result.passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/40 bg-rose-500/10"}`}>
            <div className="font-display text-3xl font-bold tabular-nums">{result.right}/{result.out_of}</div>
            <div className={result.passed ? "text-emerald-300" : "text-rose-300"}>{result.passed ? "Passed" : "Not yet. Read why below, then try five new members."}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              {result.passed && level < 3 && onExit && <Button onClick={() => onExit(level + 1)} className="rounded-full bg-emerald-600 hover:bg-emerald-500">
                Level {level + 1}: {level === 1 ? "pick the best reply" : "write one reply"}</Button>}
              <Button onClick={start} variant={result.passed ? "outline" : "default"} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Five new members</Button>
            </div>
          </div>
        )}
      </section>
      {!quiz && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
      {quiz && quiz.questions.map((q, i) => {
        const r = byId[q.id];
        return (
          <section key={q.id} className="card-surface p-4 sm:p-5 space-y-3">
            <div className="font-display text-2xl font-bold text-emerald-300">{i + 1}</div>
            <CardInfo card={q.card} />
            <CardDay card={q.card} />
            {i === 0 && <ProteinTable foods={quiz.food_table} />}
            <div className="text-sm font-semibold text-zinc-200">Which reply do you send?</div>
            <div className="grid gap-2">
              {q.options.map((o) => {
                const picked = answers[q.id] === o.id;
                const tone = r ? (o.id === r.answer ? "border-emerald-500/60 bg-emerald-500/10" : picked ? "border-rose-500/60 bg-rose-500/10" : "border-[#262626] opacity-70")
                  : picked ? "border-white bg-white/5" : "border-[#262626] hover:border-zinc-500";
                return (
                  <label key={o.id} className={`block rounded-lg border p-3 cursor-pointer ${tone}`}>
                    <div className="flex gap-2">
                      <input type="radio" name={q.id} className="mt-1" disabled={!!r} checked={picked} onChange={() => setAnswers({ ...answers, [q.id]: o.id })} />
                      <span className="text-[15px] leading-snug whitespace-pre-wrap text-zinc-100">{o.text}</span>
                    </div>
                    {r && (o.id === r.answer || picked) && (
                      <p className={`text-sm mt-2 flex gap-1.5 ${o.id === r.answer ? "text-emerald-300" : "text-rose-300"}`}>
                        {o.id === r.answer ? <Check className="h-4 w-4 mt-0.5 shrink-0" /> : <X className="h-4 w-4 mt-0.5 shrink-0" />}{r.why[o.id]}</p>
                    )}
                  </label>
                );
              })}
            </div>
            {r && <p className="text-sm text-zinc-400 flex gap-2"><Eye className="h-4 w-4 mt-0.5 shrink-0" /><span><b>What this day was hiding:</b> {r.trap}</span></p>}
          </section>
        );
      })}
      {quiz && !result && (
        <Button onClick={submit} disabled={busy || Object.keys(answers).length < quiz.questions.length} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Mark my answers ({Object.keys(answers).length}/{quiz.questions.length})</Button>
      )}
    </div>
  );
}

// Facility heads: exam replies that closely match another coach's (course input section 5). A conversation, not a score.
export function CopyFlags() {
  const [flags, setFlags] = useState(null);
  const load = useCallback(() => getCopyFlags().then((d) => setFlags(d.flags)).catch(() => setFlags([])), []);
  useEffect(() => { load(); }, [load]);
  if (!flags?.length) return null;
  return (
    <section className="card-surface p-4 sm:p-5 space-y-3" data-testid="copy-flags">
      <div><div className="eyebrow text-amber-300">Copied replies</div>
        <p className="text-sm text-zinc-400 mt-1">Exam replies that closely match another coach's. Talk to them both before anything else; it isn't a score.</p></div>
      {flags.map((f) => (
        <div key={f.id} className={`rounded-lg border p-3 space-y-2 text-sm ${f.status === "open" ? "border-amber-500/40" : "border-[#262626] opacity-70"}`}>
          <div className="flex flex-wrap justify-between gap-2"><span className="font-semibold">{f.user_name} reply {f.reply} · {f.other_user_name} reply {f.other_reply}</span>
            <span className="text-zinc-400 tabular-nums">{Math.round(f.similarity * 100)}% the same · {new Date(f.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span></div>
          <div className="grid gap-2 sm:grid-cols-2">
            <p className="rounded bg-[#141414] p-2 whitespace-pre-wrap">{f.text}</p><p className="rounded bg-[#141414] p-2 whitespace-pre-wrap">{f.other_text}</p></div>
          {f.status === "open" ? <Button size="sm" variant="outline" className="rounded-full" onClick={() => closeCopyFlag(f.id).then(load)}>Talked to them</Button>
            : <span className="text-xs text-zinc-500">Closed by {f.closed_by}</span>}
        </div>
      ))}
    </section>
  );
}

// Where a coach stands against the certification.
function Certification({ cert }) {
  if (!cert) return null;
  const steps = [
    [`Nutrition basics: ${cert.basics}/${cert.basics_needed} quizzes passed`, cert.basics >= cert.basics_needed],
    [`Every unit mastered (level 5): ${cert.mastered}/${cert.units}`, cert.mastered >= cert.units],
    [`Five-card set passed, 4 of 5 traps caught${cert.set_score ? ` (best ${cert.set_score})` : ""}`, cert.set_passed],
  ];
  return (
    <section className={`card-surface p-5 sm:p-6 space-y-3 ${cert.certified ? "border-emerald-500/50" : ""}`} data-testid="coach-certification">
      <div className="flex items-center gap-2">
        <Award className={`h-6 w-6 ${cert.certified ? "text-emerald-300" : "text-zinc-500"}`} />
        <div>
          <div className="eyebrow">{cert.certified ? `Certified ${new Date(cert.certified_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}` : "Certification"}</div>
          <h3 className="font-display text-lg font-bold">LIFTR Certified Nutrition &amp; Accountability Coach</h3>
        </div>
      </div>
      <ul className="space-y-1.5 text-sm">
        {steps.map(([label, ok]) => (
          <li key={label} className={`flex items-center gap-2 ${ok ? "text-emerald-300" : "text-zinc-300"}`}>
            {ok ? <Check className="h-4 w-4" /> : <Lock className="h-4 w-4 text-zinc-500" />}{label}</li>
        ))}
      </ul>
    </section>
  );
}

// The five-card reply set: the full test, open once every unit has passed level 4.
function FiveCards({ set, onDone }) {
  const [replies, setReplies] = useState(["", "", "", "", ""]);
  const [notes, setNotes] = useState(["", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { onDone(await finishPracticeChat(set.id, { replies, notes })); } catch (e) { err(e, "Couldn't send your replies"); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="five-cards">
      <p className="text-sm text-zinc-300">{set.brief} Write them yourself. A pasted answer tells you nothing about where you are.</p>
      <p className="text-sm text-amber-300" data-testid="ai-checker-warning">An AI checker reads every reply. It flags replies written by AI, or that could have been written without reading this member's day. We also compare answers between coaches, because copying happened in the last assignment.</p>
      <ProteinTable table={set.protein_table} foods={set.food_table} />
      {set.cards.map((c, i) => (
        <section key={i} className="card-surface p-4 sm:p-5 space-y-3">
          <div className="font-display text-2xl font-bold text-emerald-300">{i + 1}</div>
          <CardInfo card={c} />
          <CardDay card={c} />
          <Textarea value={replies[i]} onChange={(e) => setReplies(replies.map((r, j) => (j === i ? e.target.value : r)))} rows={4}
            placeholder={`Your reply to ${c.person.name}…`} />
          <Textarea value={notes[i]} onChange={(e) => setNotes(notes.map((r, j) => (j === i ? e.target.value : r)))} rows={1}
            placeholder="Anything else you'd do today? (optional, not sent)" />
        </section>
      ))}
      <Button onClick={submit} disabled={busy || replies.some((r) => !r.trim())} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Send all five for marking</Button>
    </div>
  );
}

function SetResult({ set }) {
  const r = set.rating;
  return (
    <div className="space-y-4">
      <div className="font-display text-5xl font-bold tabular-nums">{r.overall_score}<span className="text-xl text-zinc-500">/100</span></div>
      <p className="text-zinc-300">{r.summary} {r.total}/{r.out_of} marks.</p>
      {r.capped && <p className="text-sm text-rose-300">{r.capped}</p>}
      {!!r.copied?.length && <p className="text-sm text-amber-300">{r.copied.map((x) => `Reply ${x.reply}`).join(", ")} closely {r.copied.length === 1 ? "matches" : "match"} another coach's reply. Your facility head will talk to you about it.</p>}
      {set.cards.map((c, i) => (
        <section key={i} className="card-surface p-4 sm:p-5 space-y-3">
          <div className="font-semibold">{i + 1}. {c.person.name}</div>
          <p className="text-sm whitespace-pre-wrap rounded-lg bg-[#005c4b]/30 p-3">{set.replies?.[i]}</p>
          <Marks r={r.cards[i]} trap={set.traps?.[i]} />
        </section>
      ))}
    </div>
  );
}

export function FullChats() {
  const [home, setHome] = useState(null);
  const [set, setSet] = useState(null);
  const load = useCallback(() => getMyPracticeChats().then(setHome).catch((e) => err(e, "Couldn't load")), []);
  useEffect(() => { load(); }, [load]);
  const poll = useCallback((id) => {
    let tries = 0;
    const tick = () => getPracticeChat(id).then((c) => { setSet(c); if (c.status === "rating" && ++tries < 80) setTimeout(tick, 4000); else load(); })
      .catch(() => setTimeout(tick, 5000));
    tick();
  }, [load]);
  if (!home) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  if (set) {
    return (
      <div className="space-y-4">
        {set.status === "live" && <FiveCards set={set} onDone={(c) => { setSet(c); poll(c.id); }} />}
        {set.status === "rating" && <p className="flex items-center gap-2 text-zinc-300"><Loader2 className="h-4 w-4 animate-spin" />Marking all five. About two minutes.</p>}
        {set.status === "error" && <div className="space-y-2"><p className="text-rose-300">{set.error}</p><Button onClick={() => reratePracticeChat(set.id).then(() => poll(set.id))}>Try marking again</Button></div>}
        {set.status === "rated" && <SetResult set={set} />}
        <Button variant="outline" className="rounded-full" onClick={() => { setSet(null); load(); }}>Back</Button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Certification cert={home.certification} />
      <section className="card-surface p-5 sm:p-6 space-y-3">
        <div className="eyebrow">The certification test</div>
        <h3 className="font-display text-lg font-bold">Five-card reply set</h3>
        <p className="text-sm text-zinc-400 max-w-2xl">Five members, five days, five different traps: three from the core units and two from OARS. One reply each. To pass you need {home.pass_mark}+ overall, at least 4 of the 5 traps caught, and nothing made up. You see what each card was hiding after it's marked. New cards every time.</p>
        <p className="text-sm text-amber-300">An AI checker reads every reply. It flags replies written by AI, or that could have been written without reading this member's day. We also compare answers between coaches, because copying happened in the last assignment.</p>
        {!home.full_unlocked && <p className="text-sm text-amber-300">Opens once all {home.full_unlock_parts} units have passed level 4. You've finished {home.parts_done} of {home.full_unlock_parts}.</p>}
        <Button onClick={() => startPracticeChat(0, 0).then(setSet).catch((e) => err(e, "Couldn't start"))} disabled={!home.full_unlocked || !home.ready}
          className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6"><MessageSquare className="h-4 w-4 mr-2" />Start a five-card set</Button>
      </section>
      {!!home.recent.length && (
        <div className="card-surface divide-y divide-[#1f1f1f]">
          {home.recent.map((c) => (
            <button key={c.id} type="button" onClick={() => getPracticeChat(c.id).then(setSet)} className="w-full p-3 flex flex-wrap justify-between gap-2 text-sm text-left hover:bg-white/[0.02]">
              <span>Five-card set · {new Date(c.started_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
              <span className={`tabular-nums ${c.set_passed ? "text-emerald-300" : "text-zinc-400"}`}>{c.rating ? `${c.rating.overall_score}/100${c.set_passed ? " · passed" : ""}` : c.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
